import React, { useState } from 'react';
import { List, useTable, EditButton, ShowButton, DeleteButton } from '@refinedev/antd';
import { Table, Space, Tag, Input, Select } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { LlmProviderDto, LlmProviderType } from '@wellverse/shared-ts';

const typeColors: Record<LlmProviderType, string> = {
  [LlmProviderType.OPENAI]: '#52c41a',
  [LlmProviderType.ANTHROPIC]: '#722ed1',
  [LlmProviderType.CUSTOM]: '#1890ff',
};

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

export const LlmProviderList: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [typeFilter, setTypeFilter] = useState<string | undefined>(undefined);

  const { tableProps } = useTable<LlmProviderDto>({
    resource: 'llm-providers',
    filters: {
      permanent: [
        ...(searchText ? [{ field: 'search', operator: 'contains' as const, value: searchText }] : []),
        ...(typeFilter ? [{ field: 'type', operator: 'eq' as const, value: typeFilter }] : []),
      ],
    },
    pagination: { current: 1, pageSize: 20 },
    queryOptions: { keepPreviousData: true },
  });

  return (
    <List resource="llm-providers">
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
        <Input.Search
          prefix={<SearchOutlined />}
          placeholder="Search providers..."
          defaultValue={searchText}
          onSearch={(value) => setSearchText(value)}
          allowClear
          style={{ width: 320 }}
        />
        <Select
          placeholder="Filter by type"
          allowClear
          style={{ width: 200 }}
          onChange={(value) => setTypeFilter(value)}
          options={[
            { label: 'OpenAI', value: LlmProviderType.OPENAI },
            { label: 'Anthropic', value: LlmProviderType.ANTHROPIC },
            { label: 'Custom', value: LlmProviderType.CUSTOM },
          ]}
        />
      </div>
      <Table {...tableProps} rowKey="id" scroll={{ x: 1200 }}>
        <Table.Column dataIndex="name" title="Name" />
        <Table.Column
          dataIndex="type"
          title="Type"
          render={(type: LlmProviderType) => {
            const color = typeColors[type] || '#898989';
            return (
              <Tag style={tagStyle(color)}>
                {type}
              </Tag>
            );
          }}
        />
        <Table.Column dataIndex="defaultModel" title="Default Model" />
        <Table.Column
          dataIndex="baseUrl"
          title="Base URL"
          render={(url: string) => url || '—'}
        />
        <Table.Column
          dataIndex="isActive"
          title="Active"
          render={(isActive: boolean) => (
            <Tag style={tagStyle(isActive ? '#3ecf8e' : '#898989')}>
              {isActive ? 'Active' : 'Inactive'}
            </Tag>
          )}
        />
        <Table.Column dataIndex="priority" title="Priority" />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          render={(_: any, record: LlmProviderDto) => (
            <Space>
              <EditButton hideText size="small" recordItemId={record.id} resource="llm-providers" />
              <ShowButton hideText size="small" recordItemId={record.id} resource="llm-providers" />
              <DeleteButton hideText size="small" recordItemId={record.id} resource="llm-providers" />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
